import React from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { T, FONT_MONO } from '../constants/tokens';

const useStyles = makeStyles({
  // The entity forms are cards of their own on a page. Inside the dialog they
  // lose that chrome, so there is one card, not a card in a card.
  body: {
    '& > form': {
      background: 'transparent !important',
      border: '0 !important',
      borderRadius: '0 !important',
      padding: '0 !important',
      maxWidth: 'none !important'
    }
  }
});

const footerButton = (tone) => ({
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  height: 40,
  padding: '0 20px',
  borderRadius: 6,
  border: '1px solid ' + T.hairline,
  background: T.elevated,
  color: tone === 'danger' ? '#ee0000' : T.ink,
  cursor: 'pointer'
});

/**
 * The design's add / edit record dialog: the table's name as an eyebrow,
 * "Add {record}" or "Edit {record}" as the title, then the entity's own form,
 * which keeps its validation and its Save button. Editing adds "Delete
 * record" at the foot.
 *
 * Floor plans need room, so tables whose forms place things on one pass a
 * wider `width`.
 */
export default function CrudDialog({ open, onClose, eyebrow, title, children, onDelete = null, width = 640 }) {
  const classes = useStyles();

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth={false}
      scroll='body'
      aria-label={title}
      PaperProps={{
        style: {
          width: 'min(' + width + 'px, calc(100% - 32px))',
          margin: '48px 16px',
          borderRadius: 8,
          border: '1px solid ' + T.hairline,
          background: T.elevated
        }
      }}
    >
      <div style={{ padding: '32px 32px 24px', borderBottom: '1px solid ' + T.hairlineSoft, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body }}>{eyebrow}</div>
          <h2 style={{ margin: '8px 0 0', fontSize: 32, fontWeight: 600, lineHeight: '40px', letterSpacing: '-1.28px', color: T.ink }}>{title}</h2>
        </div>
        <button
          type='button'
          aria-label='Close'
          onClick={onClose}
          style={{ flex: 'none', width: 32, height: 32, border: 0, background: 'transparent', color: T.body, fontSize: 20, cursor: 'pointer', borderRadius: 6 }}
        >
          &times;
        </button>
      </div>

      <div className={classes.body} style={{ padding: '8px 32px 24px' }}>
        {children}
      </div>

      {onDelete &&
        <div style={{ padding: '16px 32px 24px', borderTop: '1px solid ' + T.hairlineSoft, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <button type='button' style={footerButton('danger')} onClick={onDelete}>Delete record</button>
          <div style={{ flex: '1 0 12px' }} />
          <button type='button' style={footerButton()} onClick={onClose}>Cancel</button>
        </div>}
    </Dialog>
  );
}

CrudDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  onDelete: PropTypes.func,
  width: PropTypes.number
};
